import { motion } from 'framer-motion';
import { ArrowRight, FileText, Zap, Shield, TrendingUp } from 'lucide-react';
import { useState, useEffect } from 'react';

// Countdown Timer Hook
const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  function calculateTimeLeft() {
    const difference = new Date(targetDate) - new Date();
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return timeLeft;
};

const stats = [
  { label: 'TPS Speed', value: '3,000.00', icon: Zap },
  { label: 'Low Fees', value: '$0.001', icon: TrendingUp },
  { label: 'PoA Secure', value: '100%', icon: Shield },
];

// Launch Date: April 30, 2026
const LAUNCH_DATE = '2026-04-30T00:00:00';

const CountdownTimer = () => {
  const timeLeft = useCountdown(LAUNCH_DATE);

  const timeUnits = [
    { label: 'Days', value: timeLeft.days },
    { label: 'Hours', value: timeLeft.hours },
    { label: 'Minutes', value: timeLeft.minutes },
    { label: 'Seconds', value: timeLeft.seconds },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1 }}
      className="text-center"
      data-testid="countdown-timer"
    >
      <p className="text-sm uppercase tracking-[0.2em] text-blue-400 mb-4 font-medium">
        Project Launching In
      </p>
      <div className="flex items-center justify-center gap-2 sm:gap-4 md:gap-6 flex-wrap">
        {timeUnits.map((unit, index) => (
          <div key={unit.label} className="flex items-center gap-2 sm:gap-3 md:gap-6">
            <motion.div
              className="flex flex-col items-center"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 1.1 + index * 0.1 }}
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl sm:rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-[0_0_30px_rgba(0,85,255,0.2)]">
                <span className="text-lg sm:text-2xl md:text-4xl font-bold text-white tabular-nums">
                  {String(unit.value).padStart(2, '0')}
                </span>
              </div>
              <span className="text-xs sm:text-xs md:text-sm text-zinc-400 mt-1 sm:mt-2 uppercase tracking-wider">
                {unit.label}
              </span>
            </motion.div>
            {index < timeUnits.length - 1 && (
              <span className="text-lg sm:text-2xl md:text-3xl font-bold text-blue-400 mb-4 md:mb-6">:</span>
            )}
          </div>
        ))}
      </div>
      <p className="text-zinc-500 mt-4 text-sm">
        Launch Date: April 30, 2026
      </p>
    </motion.div>
  );
};

export const Hero = () => {
  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden hero-bg"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/e8f5391f-27da-4e56-b4ea-a3f7a756238b/images/5466179e5a4cba570bba1eee1ddce9ec1fe7fabaf49116eb83505724c4e74ed3.png')`,
          backgroundAttachment: 'fixed',
        }}
      >
        <div className="absolute inset-0 bg-black/60 dark:bg-black/70" />
      </div>

      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-500/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              bottom: '-10px',
            }}
            animate={{
              y: [0, -1000],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 5 + Math.random() * 5,
              repeat: Infinity,
              delay: Math.random() * 5,
            }}
          />
        ))}
      </div>

      {/* Network grid pattern */}
      <div className="absolute inset-0 network-grid opacity-20" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Badge */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            <span className="text-sm font-medium text-blue-400">
              Next-Gen Proof of Authority Blockchain with AI
            </span>
          </motion.div>

          {/* Headline */}
          <h1 className="text-3xl sm:text-4xl lg:text-7xl font-black tracking-tighter text-white mb-4 sm:mb-6 leading-tight">
            Powering the Future of{' '}
            <span className="gradient-text">Decentralization</span>
            <span className="block text-xl sm:text-2xl lg:text-4xl mt-1 sm:mt-2 text-blue-400">with AI</span>
          </h1>

          {/* Subtext */}
          <p className="text-base sm:text-lg lg:text-xl text-zinc-400 max-w-3xl mx-auto mb-8 sm:mb-12 leading-relaxed">
            Polk Chain is a next-generation blockchain ecosystem featuring lightning-fast
            transactions, minimal fees, and enterprise-grade security. Experience the
            complete Web3 stack with Polk Wallet, Polk Swap, and native DApps.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-16">
            <a href="https://p2aipolkchain.com/polkWallet.apk">
            <motion.button
              whileHover={{ scale: 1.02, boxShadow: '0 0 40px rgba(0,85,255,0.5)' }}
              whileTap={{ scale: 0.98 }}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white rounded-full px-6 sm:px-8 py-3 sm:py-4 font-semibold text-sm sm:text-base transition-all shadow-[0_0_20px_rgba(0,85,255,0.3)]"
              data-testid="launch-app-btn"
            >
              Launch App
              <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5" />
            </motion.button>
            </a>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white rounded-full px-6 sm:px-8 py-3 sm:py-4 font-semibold text-sm sm:text-base backdrop-blur-md border border-white/20 transition-all"
              data-testid="whitepaper-btn"
            >
              <FileText className="w-4 sm:w-5 h-4 sm:h-5" />
              Read Whitepaper
            </motion.button>
          </div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col sm:grid sm:grid-cols-3 items-center justify-center gap-4 sm:gap-6 lg:gap-12 mb-12"
            data-testid="hero-stats"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8 + index * 0.1 }}
                className="flex items-center gap-3 sm:gap-4 stats-card rounded-2xl px-4 sm:px-6 py-3 sm:py-4 w-full sm:w-auto"
              >
                <div className="p-2 sm:p-3 rounded-xl bg-blue-500/20 flex-shrink-0">
                  <stat.icon className="w-5 sm:w-6 h-5 sm:h-6 text-blue-400" />
                </div>
                <div className="text-left">
                  <div className="text-xl sm:text-2xl font-bold text-white">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-zinc-500">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Countdown Timer */}
          <CountdownTimer />

        </motion.div>



        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-1"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1.5 h-3 rounded-full bg-white/50"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
